import { Box, Field, Flex, Select, SelectProps, Text } from '@workwhile/ui'
import { getTipHistory } from 'api/worker'
import { format } from 'date-fns'
import { formatCurrency } from 'lib/string'
import { QueryFnData } from 'queries/types'
import { useTipHistory } from 'queries/worker'
import { useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useTheme } from 'styled-components'

type TipHistoryItem = QueryFnData<typeof getTipHistory>['workHistory'][number]

type Props = {
  workerId: number
} & SelectProps<TipHistoryItem>

/**
 * @example
 * For a shift that starts on September 15, 2026, at 8:00 AM, the output will be:
 * "September 15, 2026 Sep 15 8:00 AM 8 AM 09/15/2026 9/15/26"
 */
const _SEARCHABLE_DATE_FORMAT =
  'MMMM d, yyyy MMM d h:mm a h a MM/dd/yyyy M/d/yy'

/**
 * Given a worker ID, return a list of shifts that are eligible for tipping.
 * Select a shift from the dropdown menu to populate a `workId` field.
 * Dropdown menu is searchable by location name, start/end times, and shift id.
 * Dropdown menu is safe to be used within modals and will expand modal content height to scroll when opened.
 * Requires form context container.
 */
export const SelectTipShift = ({ workerId, ...selectOptions }: Props) => {
  const theme = useTheme()
  const { control } = useFormContext()
  const { field, fieldState } = useController({
    name: 'workId',
    control: control,
  })

  const { data, isLoading: isTipHistoryLoading } = useTipHistory(
    { workerId },
    {
      enabled: !!workerId,
    }
  )
  const options = data?.workHistory?.filter((tip) => !!tip.work.shift) ?? []

  /** Join all searchable fields */
  const getOptionLabel: SelectProps<TipHistoryItem>['getOptionLabel'] = (
    option
  ) => {
    const shift = option?.work?.shift
    return shift
      ? [
          shift.location?.name,
          shift.position.name,
          format(new Date(shift.startsAt), _SEARCHABLE_DATE_FORMAT),
          format(new Date(shift.endsAt), _SEARCHABLE_DATE_FORMAT),
          `#${shift.shiftId}`,
        ].join(' ')
      : ''
  }

  /** Match any text in the option label against the search term */
  const filterOption: SelectProps<TipHistoryItem>['filterOption'] = (
    option,
    input
  ) => {
    const searchTerm = input.toLowerCase()
    return getOptionLabel(option.data).toLowerCase().includes(searchTerm)
  }

  /** Format option label for display in the dropdown menu options
   * @example
   * **The Location Name** (Shift #1234)
   * May 15, 2026 8:00 AM - 4:00 PM     Tip $10.00
   */
  const formatOptionLabel: SelectProps<TipHistoryItem>['formatOptionLabel'] = (
    option
  ) => {
    const shift = option.work.shift
    const tipAmount = option.totalPaymentAmount
    return (
      <Flex key={option.work.workId} flexDirection="column">
        <Flex gap={theme.space[2]}>
          <Text fontWeight="bold">
            {shift.location?.name || shift.position?.name || ''}
          </Text>
          <Text>(Shift #{shift.shiftId})</Text>
        </Flex>

        <Flex justifyContent="space-between">
          <Text>
            {`${format(
              new Date(shift.startsAt),
              'MMM d, yyyy h:mm a'
            )} - ${format(new Date(shift.endsAt), 'h:mm a')}`}
          </Text>
          {tipAmount > 0 ? <Text>Tip {formatCurrency(tipAmount)}</Text> : null}
        </Flex>
      </Flex>
    )
  }

  const withSearchEnabled = useMemo(
    () =>
      selectOptions.isSearchable
        ? ({
            backspaceRemovesValue: false,
            escapeClearsValue: true,
            filterOption,
            formatOptionLabel,
            getOptionLabel,
            isClearable: true,
            isSearchable: true,
          } satisfies Partial<SelectProps<TipHistoryItem>>)
        : {},
    [selectOptions.isSearchable]
  )

  return (
    <Box position="relative">
      <Field
        label={<Text fontWeight="bold">Shift</Text>}
        error={fieldState.error?.message}
      >
        <Text color={theme.colors.lightText} mb={2}>
          Only shifts completed within the past 20 days are eligible for tips
        </Text>
        <Select<TipHistoryItem>
          {...field}
          value={
            options.find((option) => option.work.workId === field.value) ?? null
          }
          onChange={(option) => {
            field.onChange(option?.work.workId ?? null)
          }}
          captureMenuScroll={
            false
          } /** Allow scrolling above/below the menu when opened */
          getOptionValue={(option) => String(option.work.workId)}
          isDisabled={!options.length}
          isLoading={isTipHistoryLoading}
          noOptionsMessage={() => 'No eligible shifts found'}
          options={options}
          placeholder={
            options?.length
              ? 'Select an eligible shift'
              : 'No eligible shifts found'
          }
          block
          styles={{
            menu: (base) => ({
              ...base,
              position:
                'relative' /** Extend modal content height so dropdown options aren't clipped */,
              left: '2px' /** Offset box-shadow so the visual border doesn't clip */,
              maxWidth:
                '99%' /** Offset box-shadow so the visual border doesn't clip */,
            }),
          }}
          {...withSearchEnabled}
          {...selectOptions}
        />
      </Field>
    </Box>
  )
}
